import type { LearningLanguage, VerbsData } from '../types';
import { t } from '../i18n/translations';
import type { Language } from '../i18n/translations';
import { buildVerbCardHref, getVerbPracticeConfig, getVerbPracticeGroupIds } from '../utils/verbPractice';

interface VerbColumnsProps {
  verbsData: VerbsData;
  deselectedVerbs: Set<string>;
  collapsed: boolean;
  verbMode: boolean;
  onToggleVerb: (key: string) => void;
  onToggleVerbGroup: (group: string) => void;
  onSelectVerbGroup?: (group: string) => void;
  onDeselectVerbGroup?: (group: string) => void;
  learningLanguage?: LearningLanguage;
  language?: Language;
}

export default function VerbColumns({
  verbsData,
  deselectedVerbs,
  collapsed,
  verbMode,
  onToggleVerb,
  onToggleVerbGroup,
  onSelectVerbGroup,
  onDeselectVerbGroup,
  learningLanguage = 'pl',
  language = 'en',
}: VerbColumnsProps) {
  const groups = getVerbPracticeGroupIds(verbsData, learningLanguage);
  const config = getVerbPracticeConfig(learningLanguage);

  return (
    <div
      id="verb-columns"
      className={`verb-columns${!verbMode ? ' hidden' : ''}${collapsed ? ' collapsed' : ''}`}
    >
      {groups.map((group) => {
        const verbs = verbsData[group] || [];
        const meta = config.groupMeta[group];
        const total = verbs.length;
        let selected = 0;
        for (const v of verbs) {
          if (!deselectedVerbs.has(`${group}::${v.inf}`)) selected++;
        }
        const allSelected = selected === total && total > 0;

        return (
          <div className="verb-col" data-group={group} key={group}>
            <div className="verb-col-header">
              <h3>
                {meta?.columnLabel || group} <span className="verb-col-pattern">{meta?.pattern || ''}</span>
              </h3>
              <span className="verb-col-actions">
                {!allSelected && (
                  <button
                    className="btn btn-sm verb-col-toggle"
                    data-group={group}
                    data-action="select"
                    onClick={() => onSelectVerbGroup ? onSelectVerbGroup(group) : onToggleVerbGroup(group)}
                  >
                    {t(language, 'verb.selectAll')}
                  </button>
                )}
                {selected > 0 && (
                  <button
                    className="btn btn-sm btn-outline verb-col-toggle"
                    data-group={group}
                    data-action="deselect"
                    onClick={() => onDeselectVerbGroup ? onDeselectVerbGroup(group) : onToggleVerbGroup(group)}
                  >
                    {t(language, 'verb.deselectAll')}
                  </button>
                )}
              </span>
            </div>
            <div className="verb-col-list" id={`verb-list-${group}`}>
              {verbs.map((v) => {
                const key = `${group}::${v.inf}`;
                const checked = !deselectedVerbs.has(key);
                return (
                  <div className="verb-row" key={key}>
                    <input
                      type="checkbox"
                      checked={checked}
                      data-key={key}
                      data-group={group}
                      onChange={() => onToggleVerb(key)}
                    />
                    <a
                      className="verb-inf"
                      href={buildVerbCardHref(group, v.inf, learningLanguage)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {v.inf}
                    </a>
                    <span className="verb-ru">{v.ru}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
